import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
} from '@mui/material';
import { makeStyles, TextButton } from '../../../themes';
import { FormikProps, useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { SelectInput, TextInput } from '../inputs';
import {
  useDispatch,
  getInventoryItemDetails,
  editInventoryItem,
} from '../../../state';
import { useIsMobile, useRefresh } from '../../../hooks';
import { MobileModalForm } from '../../../themes/mobile/MobileModalForm';
import { InventoryPhysicalItems } from '../../../pages/inventory/InventoryPhysicalItems';
import { InfoIcon } from '../../../assets';
import {
  AddPhysicalItemsToWarehouseLocationRequest,
  EditInventoryItemRequest,
  WarehouseLocationInventoryItemAPI,
} from '../../../types';
import { InfoMessage } from '../InfoMessage';

interface EditInventoryItemProps {
  customerId: number;
  customerItemId: number;
  moveableUnitId?: string | number | null;
  handleClose: () => any;
  open: boolean;
  receiptId?: number;
  receiptIds?: Array<number>;
  sku: string;
  warehouseLocationId: number;
  warehouseLocationName?: string;
  warehouseId: number;
  isFullMoveableUnit?: boolean;
}

interface RenderProps {
  receiptId?: number;
  receiptIds?: Array<number>;
  item: WarehouseLocationInventoryItemAPI | null;
  formik: FormikProps<State>;
  handleShowPhysicalItems: () => void;
  moveableUnitId?: string | number | null;
  isFullMoveableUnit?: boolean;
  isItemReady: boolean;
}

interface State {
  quantity: number | '';
  reason: string;
  receiptId: number | '';
  isFullMoveableUnit: boolean;
}

type View = 'details' | 'physicalItems';

const initialState: State = {
  quantity: '',
  reason: '',
  receiptId: '',
  isFullMoveableUnit: false,
};

export function EditInventoryItemForm({
  customerId,
  customerItemId,
  open,
  handleClose,
  moveableUnitId,
  receiptId,
  receiptIds,
  sku,
  warehouseId,
  warehouseLocationName,
  warehouseLocationId,
  isFullMoveableUnit: isFullMoveableUnitProp,
}: EditInventoryItemProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const isMobile = useIsMobile();

  const [view, setView] = useState<View>('details');
  const [item, setItem] = useState<WarehouseLocationInventoryItemAPI | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [muPhysicalItemsToEdit, setMuPhysicalItemsToEdit] =
    useState<AddPhysicalItemsToWarehouseLocationRequest | null>(null);
  const isAllocated = (item?.allocatedQuantity ?? 0) > 0;

  useEffect(() => {
    (async function () {
      const { data } = await dispatch(
        getInventoryItemDetails(
          warehouseLocationId ?? warehouseLocationName,
          customerItemId,
          {
            moveableUnitID: moveableUnitId ? moveableUnitId : undefined,
            receiptID: receiptId,
            warehouseID: warehouseId,
          },
        ),
      );
      if (data) {
        setItem(data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use the MU ID from the details endpoint (covers internal MUs whose list-row has null MU)
  const effectiveMoveableUnitId = useMemo(
    () => item?.moveableUnitID ?? moveableUnitId ?? undefined,
    [item?.moveableUnitID, moveableUnitId],
  );

  const handleSubmit = useCallback(
    async (values: State) => {
      if (!item || isAllocated) return;
      const { quantity, reason, receiptId: transferReceiptId } = values;
      setSubmitting(true);
      const payload: EditInventoryItemRequest = {
        requestType: 'Override',
        receiptID: transferReceiptId || undefined,
        warehouseLocationID: warehouseLocationId,
        warehouseLocationName,
        warehouseID: warehouseId,
        customerID: customerId,
        customerItemID: customerItemId,
        sku,
        moveableUnitID: effectiveMoveableUnitId
          ? effectiveMoveableUnitId
          : undefined,
        quantity: quantity || 0,
        reason,
        physicalItemsToUpdate: muPhysicalItemsToEdit,
        isFullMoveableUnit: values.isFullMoveableUnit || undefined,
      };

      const { error } = await dispatch(editInventoryItem(payload, true));
      setSubmitting(false);
      if (!error) {
        refresh();
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      warehouseId,
      warehouseLocationId,
      warehouseLocationName,
      customerItemId,
      effectiveMoveableUnitId,
      muPhysicalItemsToEdit,
      sku,
      item,
      isAllocated,
    ],
  );

  const formik = useFormik<State>({
    initialValues: {
      ...initialState,
      receiptId: receiptId || '',
      isFullMoveableUnit: !!isFullMoveableUnitProp,
    },
    enableReinitialize: false,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  const initialReceiptId = useRef(formik.values.receiptId);
  useEffect(() => {
    if (
      formik.values.receiptId &&
      formik.values.receiptId !== initialReceiptId.current
    ) {
      (async function () {
        const { data } = await dispatch(
          getInventoryItemDetails(
            warehouseLocationId ?? warehouseLocationName,
            customerItemId,
            {
              moveableUnitID: moveableUnitId ? moveableUnitId : undefined,
              receiptID: Number(formik.values.receiptId),
              warehouseID: warehouseId,
            },
          ),
        );
        if (data) {
          setItem(data);
        }
      })();
    }
  }, [
    formik.values.receiptId,
    dispatch,
    warehouseLocationId,
    warehouseLocationName,
    customerItemId,
    moveableUnitId,
    warehouseId,
  ]);

  // Receipt must be selected when multiple exist and none was pre-selected
  const requiresReceiptSelection =
    !receiptId && !!receiptIds?.length && receiptIds.length > 1;
  const receiptSelected = !!formik.values.receiptId;
  const isItemReady =
    item != null && (!requiresReceiptSelection || receiptSelected);

  const handleShowDetails = useCallback(() => {
    setView('details');
  }, []);

  const handleShowPhysicalItems = useCallback(() => {
    setView('physicalItems');
  }, []);

  const handleGetPhysicalItemsToTransfer = useCallback(
    (payload: AddPhysicalItemsToWarehouseLocationRequest) => {
      setMuPhysicalItemsToEdit(payload);
      setView('details');
    },
    [],
  );

  if (isMobile) {
    return (
      <MobileModalForm
        open={open}
        handleClose={handleClose}
        callback={formik.handleSubmit}
        disableSubmit={submitting || !isItemReady || isAllocated}
        btnText='Transfer'
        submitting={submitting}
        title='Edit item'
      >
        {view === 'physicalItems' &&
        warehouseLocationId &&
        customerItemId &&
        formik.values.receiptId ? (
          <InventoryPhysicalItems
            receiptId={formik.values.receiptId}
            warehouseLocationId={warehouseLocationId}
            moveableUnitId={effectiveMoveableUnitId}
            customerItemId={customerItemId}
            sku={sku}
            handleBack={handleShowDetails}
            canEdit
            handleSubmitOverride={handleGetPhysicalItemsToTransfer}
            updatedSkuQuantity={formik.values.quantity}
            originalSkuQuantity={
              item?.totalPrimaryOnHand ?? item?.primaryQuantityRemaining
            }
          />
        ) : (
          <RenderEditForm
            formik={formik}
            receiptIds={receiptIds}
            receiptId={receiptId}
            item={item}
            handleShowPhysicalItems={handleShowPhysicalItems}
            moveableUnitId={effectiveMoveableUnitId}
            isFullMoveableUnit={isFullMoveableUnitProp}
            isItemReady={isItemReady}
          />
        )}
      </MobileModalForm>
    );
  }

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Edit inventory stock</span>}
      callback={formik.handleSubmit}
      btnText='EDIT ITEM'
      typeSubmit
      maxWidth='md'
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
      disableSubmit={submitting || !isItemReady || isAllocated}
    >
      {view === 'physicalItems' &&
      warehouseLocationId &&
      customerItemId &&
      formik.values.receiptId ? (
        <InventoryPhysicalItems
          receiptId={formik.values.receiptId}
          warehouseLocationId={warehouseLocationId}
          moveableUnitId={effectiveMoveableUnitId}
          customerItemId={customerItemId}
          sku={sku}
          handleBack={handleShowDetails}
          canEdit
          handleSubmitOverride={handleGetPhysicalItemsToTransfer}
          updatedSkuQuantity={formik.values.quantity}
          originalSkuQuantity={
            item?.totalPrimaryOnHand ?? item?.primaryQuantityRemaining
          }
        />
      ) : (
        <Fragment>
          <div className={classes.locationContainer}>
            {sku && <div>{sku}</div>}
            {sku && warehouseLocationName && <span className={classes.dot} />}
            {warehouseLocationName && (
              <div className={classes.location}>{warehouseLocationName}</div>
            )}
          </div>
          <RenderEditForm
            formik={formik}
            receiptIds={receiptIds}
            receiptId={receiptId}
            item={item}
            handleShowPhysicalItems={handleShowPhysicalItems}
            moveableUnitId={effectiveMoveableUnitId}
            isFullMoveableUnit={isFullMoveableUnitProp}
            isItemReady={isItemReady}
          />
        </Fragment>
      )}
    </FormModal>
  );
}

function RenderEditForm({
  formik,
  receiptIds,
  receiptId,
  item,
  handleShowPhysicalItems,
  moveableUnitId,
  isFullMoveableUnit,
  isItemReady,
}: RenderProps) {
  const { classes, css } = useStyles();
  const isMobile = useIsMobile();

  const {
    primaryQuantityRemaining: skuQuantity = 0,
    skuPhysicalItemsQuantity = 0,
    totalPrimaryOnHand = 0,
    unavailableQuantity = 0,
  } = item || {};

  const maxQtyAllowedToRemove = skuQuantity - skuPhysicalItemsQuantity;
  const otherOnHand = totalPrimaryOnHand - skuQuantity;
  const unavailableMinimum = Math.max(0, unavailableQuantity - otherOnHand);
  const minQuantity = Math.max(unavailableMinimum, skuPhysicalItemsQuantity);
  const isAllocated = (item?.allocatedQuantity ?? 0) > 0;
  const canEdit = isItemReady && !isAllocated;

  return (
    <div>
      {isAllocated ? (
        <Alert severity='error' className={css({ marginBottom: 16 })}>
          This inventory group is allocated to an order and cannot be edited.
          Remove the allocation(s) before editing.
        </Alert>
      ) : (
        <InfoMessage
          className={css({ marginBottom: 16 })}
          message="Tread carefully. Changes made to this inventory stock item will override current data and cannot be undone. The storage date will be changed to the date of today's cycle count."
        />
      )}
      {isItemReady && item?.totalPrimaryOnHand != null && (
        <div className={css({ marginBottom: 12, fontSize: '0.875rem' })}>
          {`On Hand: ${item.totalPrimaryOnHand} | Avail QTY: ${item.totalPrimaryOnHand - (item.unavailableQuantity ?? 0)}`}
          {(item.unavailableQuantity ?? 0) > 0 && (
            <span style={{ marginLeft: '8px', color: '#666' }}>
              {'('}
              {[
                item.allocatedQuantity
                  ? `Allocated: ${item.allocatedQuantity}`
                  : '',
                item.onHoldQuantity ? `On Hold: ${item.onHoldQuantity}` : '',
                item.stagedQuantity ? `Staged: ${item.stagedQuantity}` : '',
                item.pickJobDropsQuantity
                  ? `Pick Drops: ${item.pickJobDropsQuantity}`
                  : '',
                item.ownershipQuantity
                  ? `Order Owned: ${item.ownershipQuantity}`
                  : '',
              ]
                .filter(Boolean)
                .join(' | ')}
              {')'}
            </span>
          )}
        </div>
      )}
      <Grid container spacing={3} rowSpacing={2}>
        {!receiptId && !!receiptIds?.length && receiptIds.length > 1 && (
          <Grid
            size={{
              sm: 12,
            }}
          >
            <SelectInput
              name='receiptId'
              formikProps={formik}
              label='Select a receipt'
              options={receiptIds.map((id) => ({
                id,
                name: `Receipt ${id}`,
              }))}
              className={classes.input}
            />
          </Grid>
        )}
        <Grid
          size={{
            sm: isMobile ? 12 : 3,
          }}
        >
          <TextInput
            name='quantity'
            formikProps={formik}
            label='On hand primary QTY'
            className={classes.input}
            type='number'
            disabled={!canEdit}
            slotProps={{
              htmlInput: {
                max: skuQuantity,
                min: minQuantity,
              },
              input: {
                endAdornment:
                  skuPhysicalItemsQuantity > 0 &&
                  maxQtyAllowedToRemove < skuQuantity ? (
                    <Tooltip
                      title={`The quantity cannot be less than ${skuPhysicalItemsQuantity} because there are ${skuPhysicalItemsQuantity} physical items for this SKU. To reduce the quantity below this limit, you must also remove physical items in the inventory tracking screen.`}
                    >
                      <InfoIcon
                        className={css({
                          fontSize: 16,
                          color: '#A5AABD',
                          marginLeft: 8,
                        })}
                      />
                    </Tooltip>
                  ) : undefined,
              },
            }}
          />
        </Grid>
        <Grid
          size={{
            sm: isMobile ? 12 : 9,
          }}
        >
          <TextInput
            name='reason'
            formikProps={formik}
            label='Reason'
            className={classes.input}
            disabled={!canEdit}
          />
        </Grid>
      </Grid>
      {!!moveableUnitId &&
        isFullMoveableUnit &&
        formik.values.quantity !== '' &&
        formik.values.quantity !== skuQuantity && (
          <FormControlLabel
            control={
              <Checkbox
                disabled={!canEdit}
                checked={formik.values.isFullMoveableUnit}
                onChange={(e) =>
                  formik.setFieldValue('isFullMoveableUnit', e.target.checked)
                }
              />
            }
            label='Keep as full moveable unit'
          />
        )}
      {canEdit && formik.values.quantity !== '' && (
        <div>
          <TextButton onClick={handleShowPhysicalItems}>
            View physical items
          </TextButton>
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles({
  name: { TransferInventoryItemForm: EditInventoryItemForm },
})((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  subTitle: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    letterSpacing: 0,
    marginBottom: 16,
  },
  input: {
    width: '100%',
  },
  sectionTitle: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 0,
    marginTop: 40,
    marginBottom: 16,
    [theme.breakpoints.down('tablet')]: {
      marginTop: 16,
    },
  },
  location: {
    color: '#00C4F8',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0,
  },
  locationContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  dot: {
    height: 8,
    width: 8,
    marginRight: 8,
    borderRadius: 10,
    background: '#000000',
    margin: '0px 8px',
  },
}));

const schema = object().shape({
  quantity: string().required('Required'),
  reason: string().required('Required'),
});
